import { refreshMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/hooks/refreshMultiCartFlightItem'
import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import { FlightViewTypes } from 'constants/flight'
import { last } from 'lib/array/arrayUtils'
import type { CartPanelItem } from '../hooks/useCartPanelItems'
import { isFlightSearchExpired, isResolvedFlightJourneyId } from './flightCartPanelUtils'
import { getMultiCityGroupId, getMultiCityLegOrder } from './multiCityFlightHelpers'

type CheckoutReadyItem = CartPanelItem & { cartItem: App.MultiCartItem }

interface RefreshDeps {
  currentCurrency: string
  currentRegionCode: string
  email: string | undefined
  enableSabreContent: boolean
}

function getFlightItem(item: CheckoutReadyItem): App.Checkout.FlightItem | undefined {
  return item.productType === 'flight' && isMultiCartFlightItem(item.cartItem)
    ? (item.cartItem.item as App.Checkout.FlightItem)
    : undefined
}

function hasUnresolvedJourneyId(flight: App.Checkout.FlightItemFlight): boolean {
  return !isResolvedFlightJourneyId(flight.journeyId, flight.journeyKey)
}

function shouldRefreshMultiCityLeg(item: CheckoutReadyItem): boolean {
  const flightItem = getFlightItem(item)
  if (!flightItem?.searchId || !flightItem.expiry || isFlightSearchExpired(flightItem.expiry)) return true
  return flightItem.flights.some(hasUnresolvedJourneyId)
}

/**
 * Re-price a multi-city group the same way the Customer Portal cart
 * does: collapse the per-leg cart items back into a single multi-leg flight item
 * and run it through the shared `refreshMultiCartFlightItem`, which fires ONE
 * multi-city search over the full itinerary and matches each leg by
 * journeyKey + pairedFareId chaining. We then split the refreshed legs back out,
 * giving every leg the SAME fresh searchId (and a re-matched journey + valid
 * fare-family for that search) so checkout's per-leg journey re-validation
 * resolves instead of tripping the flight session-expired modal.
 *
 * Older trip-chat cart data can carry a future local expiry while its flight
 * rows still use journeyKey as journeyId. In that case checkout's final fare
 * selection can reject the stored row, so this refresh repairs only expired or
 * incomplete grouped multi-city items.
 *
 * @throws if the legs can't be combined or `refreshMultiCartFlightItem` can't
 *   re-match every leg - the caller turns this into a user-facing error rather
 *   than checking out a stale itinerary.
 */
async function refreshMultiCityGroup(
  legs: Array<CheckoutReadyItem>,
  deps: RefreshDeps,
): Promise<Map<string, CheckoutReadyItem>> {
  const sortedLegs = [...legs].sort((a, b) => getMultiCityLegOrder(a) - getMultiCityLegOrder(b))

  const firstCart = sortedLegs[0].cartItem as App.MultiCartFlightItem
  const firstItem = firstCart.item as App.Checkout.FlightItem

  // Each trip-chat leg cart item holds exactly one flight; stitch them in leg
  // order into the combined itinerary refreshMultiCartFlightItem expects.
  const combinedFlights = sortedLegs.map(leg => getFlightItem(leg)?.flights?.[0])
  if (combinedFlights.some(flight => !flight)) {
    throw new Error('Multi-city group is missing a leg flight; cannot refresh')
  }
  const flights = combinedFlights as Array<App.Checkout.FlightItemFlight>

  const syntheticMultiCartItem: App.MultiCartFlightItem = {
    ...firstCart,
    item: {
      ...firstItem,
      fareType: 'multiCity',
      viewType: FlightViewTypes.MULTI_CITY,
      flights,
      originAirportCode: flights[0].departingAirportCode,
      destinationAirportCode: last(flights).arrivalAirportCode,
    },
  }

  const { updatedMultiCartItem } = await refreshMultiCartFlightItem({
    multiCartItem: syntheticMultiCartItem,
    currentCurrency: deps.currentCurrency,
    currentRegionCode: deps.currentRegionCode,
    enableSabreContent: deps.enableSabreContent,
    email: deps.email,
  })

  const refreshedItem = updatedMultiCartItem.item as App.Checkout.FlightItem
  if (refreshedItem.flights.length !== sortedLegs.length || !refreshedItem.searchId) {
    throw new Error('Multi-city refresh returned an unexpected leg count')
  }

  // Split the refreshed itinerary back onto each per-leg cart item, preserving
  // the trip-chat "one cart item per leg" model but with the shared fresh search.
  const refreshedByKey = new Map<string, CheckoutReadyItem>()
  sortedLegs.forEach((leg, index) => {
    const legCart = leg.cartItem as App.MultiCartFlightItem
    const legItem = legCart.item as App.Checkout.FlightItem
    const refreshedLegItem: App.Checkout.FlightItem = {
      ...legItem,
      searchId: refreshedItem.searchId,
      expiry: refreshedItem.expiry,
      hasFinalFare: false,
      fareFetchError: undefined,
      flights: [refreshedItem.flights[index]],
    }
    const refreshedCart: App.MultiCartFlightItem = {
      ...legCart,
      item: refreshedLegItem,
      state: 'active',
      searchId: refreshedItem.searchId,
      expiry: refreshedItem.expiry,
      hasFinalFare: false,
    }
    // `leg` is known to be a flight item here (only flight groups reach this
    // path), so re-attaching flightCartItem is safe; the cast sidesteps the
    // CartPanelItem union's excess-property check.
    refreshedByKey.set(leg.key, {
      ...leg,
      cartItem: refreshedCart,
      flightCartItem: refreshedCart,
    } as CheckoutReadyItem)
  })

  return refreshedByKey
}

/**
 * Refreshes grouped multi-city flight groups in the checkout-ready item set
 * before the trip-chat cart navigates to checkout. Non-flight items and single
 * flights pass through untouched.
 *
 * @throws if a group can't be re-priced - callers should surface an error and
 *   abort checkout rather than proceed with a stale search.
 */
export async function refreshTripChatMultiCityFlights(
  activeItems: Array<CheckoutReadyItem>,
  deps: RefreshDeps,
): Promise<Array<CheckoutReadyItem>> {
  const groups = new Map<string, Array<CheckoutReadyItem>>()
  for (const item of activeItems) {
    const groupId = getMultiCityGroupId(item)
    if (!groupId) continue
    const group = groups.get(groupId)
    if (group) group.push(item)
    else groups.set(groupId, [item])
  }

  const groupedMultiCityLegs = [...groups.values()]
    .filter(legs => legs.length > 1 && legs.some(shouldRefreshMultiCityLeg))
  if (groupedMultiCityLegs.length === 0) return activeItems

  const refreshedByKey = new Map<string, CheckoutReadyItem>()
  for (const legs of groupedMultiCityLegs) {
    const refreshed = await refreshMultiCityGroup(legs, deps)
    refreshed.forEach((value, key) => refreshedByKey.set(key, value))
  }

  return activeItems.map(item => refreshedByKey.get(item.key) ?? item)
}
